import OP from '../OP.js';
import { fromBase58Check } from '../../primitives/utils.js';
import LockingScript from '../LockingScript.js';
import UnlockingScript from '../UnlockingScript.js';
import TransactionSignature from '../../primitives/TransactionSignature.js';
import { sha256 } from '../../primitives/Hash.js';
import { computeSignatureScope, resolveSourceDetails, formatPreimage } from './SignatureUtils.js';
import Signature from '../../primitives/Signature.js';
import { readyAsyncCryptoBackend, validateAsyncCryptoBytes } from '../../primitives/AsyncCryptoBackend.js';
/**
 * P2PKH (Pay To Public Key Hash) class implementing ScriptTemplate.
 *
 * This class provides methods to create Pay To Public Key Hash locking and unlocking scripts, including the unlocking of P2PKH UTXOs with the private key.
 */
export default class P2PKH {
    /**
     * Creates a P2PKH locking script for a given public key hash or address string
     *
     * @param {number[] | string} pubkeyhash or address - An array or address representing the public key hash.
     * @returns {LockingScript} - A P2PKH locking script.
     */
    lock(pubkeyhash) {
        let data;
        if (typeof pubkeyhash === 'string') {
            const hash = fromBase58Check(pubkeyhash);
            if (hash.prefix[0] !== 0x00 && hash.prefix[0] !== 0x6f) {
                throw new Error('only P2PKH is supported');
            }
            data = hash.data;
        }
        else {
            data = pubkeyhash;
        }
        if (data.length !== 20) {
            throw new Error('P2PKH hash length must be 20 bytes');
        }
        return new LockingScript([
            { op: OP.OP_DUP },
            { op: OP.OP_HASH160 },
            { op: data.length, data },
            { op: OP.OP_EQUALVERIFY },
            { op: OP.OP_CHECKSIG }
        ]);
    }
    /**
     * Creates a function that generates a P2PKH unlocking script along with its signature and length estimation.
     *
     * The returned object contains:
     * 1. `sign` - A function that, when invoked with a transaction and an input index,
     *    produces an unlocking script suitable for a P2PKH locked output.
     * 2. `estimateLength` - A function that returns the estimated length of the unlocking script in bytes.
     *
     * @param {PrivateKey} privateKey - The private key used for signing the transaction.
     * @param {'all'|'none'|'single'} signOutputs - The signature scope for outputs.
     * @param {boolean} anyoneCanPay - Flag indicating if the signature allows for other inputs to be added later.
     * @param {number} sourceSatoshis - Optional. The amount being unlocked. Otherwise the input.sourceTransaction is required.
     * @param {Script} lockingScript - Optional. The lockinScript. Otherwise the input.sourceTransaction is required.
     * @returns {Object} - An object containing the `sign` and `estimateLength` functions.
     */
    unlock(privateKey, signOutputs = 'all', anyoneCanPay = false, sourceSatoshis, lockingScript) {
        return {
            sign: async (tx, inputIndex) => {
                const signatureScope = computeSignatureScope(signOutputs, anyoneCanPay);
                const resolved = resolveSourceDetails(tx, inputIndex, sourceSatoshis, lockingScript);
                sourceSatoshis = resolved.sourceSatoshis;
                lockingScript = resolved.lockingScript;
                const preimage = formatPreimage({
                    tx,
                    inputIndex,
                    signatureScope,
                    sourceTXID: resolved.sourceTXID,
                    sourceSatoshis: resolved.sourceSatoshis,
                    lockingScript: resolved.lockingScript,
                    allInputs: resolved.allInputs
                });
                const preimageHash = sha256(preimage);
                const signingBackend = readyAsyncCryptoBackend('signDigest');
                const publicKeyBackend = readyAsyncCryptoBackend('publicKeyFromPrivate');
                const privateKeyBytes = signingBackend === undefined && publicKeyBackend === undefined
                    ? undefined
                    : Uint8Array.from(privateKey.toArray('be', 32));
                const rawSignature = signingBackend === undefined
                    ? privateKey.sign(preimageHash)
                    : Signature.fromDER(Array.from(validateAsyncCryptoBytes('signDigest', await signingBackend.signDigest(privateKeyBytes, 
                    // PrivateKey.sign hashes its argument before ECDSA signing.
                    // Preserve that historical double-SHA256 contract when passing a
                    // digest to a backend that signs the supplied bytes directly.
                    Uint8Array.from(sha256(preimageHash))))));
                const sig = new TransactionSignature(rawSignature.r, rawSignature.s, signatureScope);
                const sigForScript = sig.toChecksigFormat();
                const pubkeyForScript = publicKeyBackend === undefined
                    ? privateKey.toPublicKey().encode(true)
                    : Array.from(validateCompressedPublicKey(await publicKeyBackend.publicKeyFromPrivate(privateKeyBytes)));
                return new UnlockingScript([
                    { op: sigForScript.length, data: sigForScript },
                    { op: pubkeyForScript.length, data: pubkeyForScript }
                ]);
            },
            estimateLength: async () => {
                // public key (1+33) + signature (1+73)
                // Note: We add 1 to each element's length because of the associated OP_PUSH
                return 108;
            }
        };
    }
}
function validateCompressedPublicKey(value) {
    const bytes = validateAsyncCryptoBytes('publicKeyFromPrivate', value, 33);
    if (bytes[0] !== 0x02 && bytes[0] !== 0x03) {
        throw new Error('publicKeyFromPrivate returned an invalid compressed public key');
    }
    return bytes;
}
//# sourceMappingURL=P2PKH.js.map